import { GlobalOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Spin, Typography } from "antd";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "@/modules/auth";

import googleLogoUrl from "./assets/google-g-logo.png";
import microsoftSymbolUrl from "./assets/microsoft-symbol.svg";

interface LoginShellProps {
  children: ReactNode;
}

const ProviderButton = styled(Button)`
  && {
    align-items: center;
    background: #ffffff;
    border-color: #747775;
    color: #1f1f1f;
    display: flex;
    font-weight: 500;
    height: 46px;
    justify-content: flex-start;
    margin-left: 8px;
    width: calc(100% - 8px);
  }

  &&:hover,
  &&:focus {
    background: #f8faff;
    border-color: #747775;
    color: #1f1f1f;
  }
`;

const ProviderIcon = styled.img`
  height: 19px;
  margin-right: 10px;
  object-fit: contain;
  width: 19px;
`;

const LanguageButton = styled(Button)`
  && {
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
    padding: 4px 8px;
    position: absolute;
    right: 20px;
    top: 20px;
  }
`;

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.resolvedLanguage?.startsWith("de") ?? false;

  return (
    <LanguageButton
      aria-label={String(t("loginLanguageLabel"))}
      icon={<GlobalOutlined />}
      onClick={() => void i18n.changeLanguage(isGerman ? "en" : "de")}
      type="text"
    >
      {t("loginLanguage")}
    </LanguageButton>
  );
};

const LoginShell = ({ children }: LoginShellProps) => (
  <main
    style={{
      alignItems: "center",
      background:
        "linear-gradient(145deg, rgba(53, 0, 50, 0.08), rgba(245, 245, 245, 0.35) 45%, #f5f5f5)",
      display: "flex",
      justifyContent: "center",
      minHeight: "100vh",
      padding: 24,
      width: "100%",
    }}
  >
    <Card
      bordered={false}
      bodyStyle={{ padding: "40px 36px" }}
      style={{
        borderRadius: 12,
        boxShadow: "0 18px 48px rgba(53, 0, 50, 0.12)",
        maxWidth: 420,
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
    >
      <LanguageSwitcher />
      {children}
    </Card>
  </main>
);

export const LoginPage = () => {
  const { operator, loading, error, loginWithGoogle, loginWithMicrosoft } =
    useAuth();
  const { t } = useTranslation();
  const [sessionExpired, setSessionExpired] = useState(false);

  // Surfaced once after a refresh failure logged the operator out (the
  // AuthProvider sets this flag before redirecting here).
  useEffect(() => {
    if (sessionStorage.getItem("auth:sessionExpired") === "true") {
      setSessionExpired(true);
      sessionStorage.removeItem("auth:sessionExpired");
    }
  }, []);

  if (loading) {
    return (
      <LoginShell>
        <Spin size="large" style={{ display: "block" }} />
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginTop: 16, textAlign: "center" }}
        >
          {t("loginLoading")}
        </Typography.Text>
      </LoginShell>
    );
  }

  if (operator) {
    // Check if there's a saved redirect path from a deep link
    const redirectPath = localStorage.getItem("auth:redirectPath");

    // If we have a saved path, redirect there and clean up
    if (redirectPath) {
      localStorage.removeItem("auth:redirectPath");
      return <Navigate to={redirectPath} />;
    }

    // Default redirect to home
    return <Navigate to="/" />;
  }

  return (
    <LoginShell>
      <img
        alt="smarta"
        src="https://cdn.smarta.tax/images/smarta-logo.svg"
        style={{
          display: "block",
          height: 64,
          marginBottom: 24,
          width: "auto",
        }}
      />
      <Typography.Title level={4} style={{ margin: "0 0 4px 8px" }}>
        {t("loginTitle")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: "0 0 24px 8px" }}>
        {t("loginSubtitle")}
      </Typography.Paragraph>
      {sessionExpired && (
        <Alert
          type="warning"
          showIcon
          message={t("sessionExpiredBody")}
          style={{
            marginBottom: 16,
            marginLeft: 8,
            width: "calc(100% - 8px)",
          }}
        />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{
            marginBottom: 16,
            marginLeft: 8,
            width: "calc(100% - 8px)",
          }}
        />
      )}
      <ProviderButton
        block
        icon={<ProviderIcon alt="" src={googleLogoUrl} />}
        onClick={loginWithGoogle}
        size="large"
        style={{ marginBottom: 12 }}
      >
        {t("loginWithGoogle")}
      </ProviderButton>
      <ProviderButton
        block
        icon={<ProviderIcon alt="" src={microsoftSymbolUrl} />}
        onClick={loginWithMicrosoft}
        size="large"
      >
        {t("loginWithMicrosoft")}
      </ProviderButton>
    </LoginShell>
  );
};
